import React, { Suspense, useState } from "react";
import { createRoot } from "react-dom/client";
import { ZevioRoutes } from "./Routes";
import { RefreshSession } from './p/Login';
import { BrowserRouter } from "react-router-dom";
import { MeContext } from './MeContext';
import LikeContext, { useLike } from './LikeContext';
import { APIGet } from './API';
import { TraksProvider } from './traks';
import { language_set } from './languages';
import * as Sentry from "@sentry/react";

import "./css/zevio.css";

Sentry.init({
	dsn: "https://f29fb9becba99416d85de03b2be6a2e9@o4510985648078848.ingest.de.sentry.io/4510985650503760",
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	integrations: [
		Sentry.replayIntegration(),
	],
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
});

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	componentDidCatch(error, errorInfo) {
		Sentry.captureException(error, { extra: errorInfo });
	}
	render() {
		if (this.state.hasError) {
			return (
				<div style={{ textAlign: 'center', padding: '80px 20px' }}>
					<h1>Something went wrong</h1>
					<p>Please try refreshing the page.</p>
					<button onClick={() => window.location.reload()}
						style={{ padding: '10px 24px', marginTop: '16px', cursor: 'pointer' }}>
						Refresh
					</button>
				</div>
			);
		}
		return this.props.children;
	}
}

const appRootElem = document.getElementById("app_root");
const approot = createRoot(appRootElem);

const parseIpseResponse = (ipse_me) => {
	if (ipse_me?.data?.body) {
		const user = ipse_me.data.body.user;
		if (user.roles === null) user.roles = [];
		if (user.organizers === null) user.organizers = [];
		return user;
	}
	return null;
};

const renderApp = (ipse_me) => {
	const App = (props) => {
		let [ me, setMe ] = useState(ipse_me);

		if(!me) {
			RefreshSession(false);
		} else {
			const cur_org_id = parseInt(localStorage.getItem("zevio_current_organizer_id"));

			if(me.organizers) {
				if(me.organizers.includes(cur_org_id)) {
					me.currentSelectedOrganizer = cur_org_id;
				} else {
					if (me.organizers.length) {
						me.currentSelectedOrganizer = me.organizers[0];
						localStorage.setItem("zevio_current_organizer_id", me.organizers[0]);
					} else {
						me.currentSelectedOrganizer = null
					}
				}
			}
		}

		// XXX I'd prefer to have <ZevioRoutes/> reveal the language,
		// but it's awkward because <ZevioRoutes/ is deeper in the
		// "virtual DOM" than <TraksProvider/>
		let lang = "en";
		const try_lang = window.location.pathname.split("/").slice(1)[0].toLowerCase();
		if (language_set[try_lang]) lang = try_lang;
		return (
			<ErrorBoundary>
				<TraksProvider lang={lang}>
					<MeContext.Provider value={{me, setMe}}>
						<LikeContext.Provider value={useLike(me)}>
							<BrowserRouter>
								<Suspense fallback={<div>Loading...</div>}>
									<ZevioRoutes />
								</Suspense>
							</BrowserRouter>
						</LikeContext.Provider>
					</MeContext.Provider>
				</TraksProvider>
			</ErrorBoundary>
		);
	};

	approot.render(<App/>);
};

APIGet("ipse")
	.then((res) => renderApp(parseIpseResponse(res)))
	.catch((err) => {
		Sentry.captureException(err, { extra: { context: "ipse API call failed, rendering app without session" } });
		renderApp(null);
	});
